// Detección del sitio público por el host: `colegio.heskala.site` abre el
// sitio `colegio` en `/` en vez de la app. El dominio base viene de
// `VITE_SITES_BASE_DOMAIN` (vacío = apagado, todo igual que antes). Ver
// docs/sitios-subdominio.md.
//
// Funciones puras (sin `window`) para poder probarlas; `currentSiteHost()`
// es la única que lee el navegador.

import { isReservedSiteSlug, isSiteSlugShape } from "@/convex/lib/sites/slug";

/** `?site=<slug>` en desarrollo: abre el sitio en localhost. `?site=` lo quita. */
export const DEV_SITE_PARAM = "site";
const DEV_SITE_STORAGE_KEY = "heskala.dev.site";

/**
 * Dominio base normalizado: minúsculas, sin `*.` ni puntos de los extremos ni
 * puerto. Vacío o inválido → `null` (función apagada).
 */
export function normalizeBaseDomain(raw: string | undefined | null): string | null {
  if (!raw) return null;
  const base = raw
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/\/.*$/, "")
    .replace(/:\d+$/, "")
    .replace(/^\*\./, "")
    .replace(/^\.+|\.+$/g, "");
  if (!base || !/^[a-z0-9-]+(\.[a-z0-9-]+)*$/.test(base)) return null;
  return base;
}

/** Dirección válida de un sitio (forma y no reservada), en minúsculas; si no, `null`. */
export function asSiteSlug(raw: string | undefined | null): string | null {
  if (!raw) return null;
  const slug = raw.trim().toLowerCase();
  return isSiteSlugShape(slug) && !isReservedSiteSlug(slug) ? slug : null;
}

/**
 * La dirección del sitio si `hostname` es exactamente `<slug>.<base>` (una
 * sola etiqueta debajo del dominio base). `www`, las reservadas, el dominio
 * base solo, los subdominios más profundos y cualquier otro host → `null`.
 */
export function siteSlugFromHost(hostname: string, baseDomain: string | undefined | null): string | null {
  const base = normalizeBaseDomain(baseDomain);
  if (!base) return null;
  const host = hostname.trim().toLowerCase().replace(/\.$/, "");
  const suffix = `.${base}`;
  if (!host.endsWith(suffix)) return null;
  const label = host.slice(0, -suffix.length);
  if (!label || label.includes(".")) return null;
  return asSiteSlug(label);
}

export interface SiteHostInput {
  hostname: string;
  /** `location.search` (con o sin `?`). */
  search: string;
  baseDomain: string | undefined | null;
  /** `import.meta.env.DEV`: solo entonces vale `?site=`. */
  dev: boolean;
  /** Lo elegido antes con `?site=` en esta pestaña (desarrollo). */
  remembered?: string | null;
}

export interface SiteHost {
  slug: string;
  /** `true` si viene de `?site=` (desarrollo), no del host. */
  fromDevOverride: boolean;
}

/**
 * Qué sitio sirve esta carga, o `null` para la app. En desarrollo
 * `?site=<slug>` (o lo recordado en la pestaña) tiene prioridad sobre el host
 * y `?site=` vacío vuelve a la app.
 */
export function resolveSiteHost({ hostname, search, baseDomain, dev, remembered }: SiteHostInput): SiteHost | null {
  if (dev) {
    const params = new URLSearchParams(search);
    if (params.has(DEV_SITE_PARAM)) {
      const slug = asSiteSlug(params.get(DEV_SITE_PARAM));
      return slug ? { slug, fromDevOverride: true } : null;
    }
    const kept = asSiteSlug(remembered);
    if (kept) return { slug: kept, fromDevOverride: true };
  }
  const slug = siteSlugFromHost(hostname, baseDomain);
  return slug ? { slug, fromDevOverride: false } : null;
}

/** Dominio base de sitios de este build (`VITE_SITES_BASE_DOMAIN`), o `null`. */
export const sitesBaseDomain = (): string | null => normalizeBaseDomain(import.meta.env.VITE_SITES_BASE_DOMAIN);

let current: SiteHost | null | undefined;

/**
 * El sitio de esta pestaña (se calcula una vez por carga). En desarrollo
 * recuerda `?site=` en sessionStorage para que recargar `/<página>` siga en
 * el sitio.
 */
export function currentSiteHost(): SiteHost | null {
  if (current !== undefined) return current;
  if (typeof window === "undefined") return (current = null);
  const dev = import.meta.env.DEV;
  let remembered: string | null = null;
  if (dev) {
    try {
      remembered = sessionStorage.getItem(DEV_SITE_STORAGE_KEY);
    } catch {
      // Sin sessionStorage: solo vale `?site=` en la URL.
    }
  }
  current = resolveSiteHost({
    hostname: window.location.hostname,
    search: window.location.search,
    baseDomain: import.meta.env.VITE_SITES_BASE_DOMAIN,
    dev,
    remembered,
  });
  if (dev) {
    try {
      if (current?.fromDevOverride) sessionStorage.setItem(DEV_SITE_STORAGE_KEY, current.slug);
      else sessionStorage.removeItem(DEV_SITE_STORAGE_KEY);
    } catch {
      // Nada que recordar.
    }
  }
  return current;
}

/** `true` si esta pestaña sirve un sitio público y no la app. */
export const isSiteHost = (): boolean => currentSiteHost() !== null;
